'use client';

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, clearAuthError } from '../../redux/actions/authActions';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import authService from '../../services/authService';

const LoginSchema = Yup.object().shape({
  email: Yup.string()
    .email('Adresse email invalide')
    .required('Email requis'),
  password: Yup.string()
    .required('Mot de passe requis')
});

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated } = useSelector(state => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const justRegistered = new URLSearchParams(location.search).get('registered') === 'true';

  useEffect(() => {
    // Si déjà authentifié, rediriger vers le tableau de bord
    if (isAuthenticated) {
      navigate('/dashboard');
    }
    
    // Nettoyer les erreurs à l'initialisation
    dispatch(clearAuthError());
  }, [isAuthenticated, navigate, dispatch]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setEmailNotVerified(false);
    setResendMessage('');
    try {
      await dispatch(login(values));
      navigate('/dashboard');
    } catch (err) {
      if (err.response?.data?.emailNotVerified) {
        setEmailNotVerified(true);
        setPendingEmail(values.email);
        dispatch(clearAuthError());
      }
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    try {
      const res = await authService.resendVerification(pendingEmail);
      setResendMessage(res.data?.message || 'Email de vérification envoyé.');
    } catch {
      setResendMessage('Une erreur est survenue, veuillez réessayer.');
    } finally {
      setResendLoading(false);
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <h2 className="auth-title">Connexion</h2>
      <p className="auth-subtitle">Connectez-vous à votre compte pour continuer</p>

      {justRegistered && (
        <div className="alert alert-success">
          Compte créé avec succès ! Vérifiez votre email et cliquez sur le lien d'activation avant de vous connecter.
        </div>
      )}

      {emailNotVerified ? (
        <div className="alert alert-warning" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '0.5rem' }}>
          <p style={{ margin: 0 }}><strong>Email non vérifié</strong></p>
          <p style={{ margin: 0 }}>Veuillez vérifier votre boîte mail et cliquer sur le lien d'activation.</p>
          {resendMessage ? (
            <p style={{ margin: 0 }}>{resendMessage}</p>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleResendVerification}
              disabled={resendLoading}
            >
              {resendLoading ? 'Envoi...' : 'Renvoyer l\'email de vérification'}
            </button>
          )}
        </div>
      ) : error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      <Formik
        initialValues={{ email: '', password: '' }}
        validationSchema={LoginSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <Field
                type="email"
                name="email"
                id="email"
                className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                placeholder="votre@email.com"
              />
              <ErrorMessage name="email" component="div" className="invalid-feedback" />
            </div>

            <div className="form-group">
              <label htmlFor="password">Mot de passe</label>
              <div className="password-field">
                <Field
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  className={`form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                  placeholder="Mot de passe"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={toggleShowPassword}
                >
                  <i className="material-icons">
                    {showPassword ? 'visibility_off' : 'visibility'}
                  </i>
                </button>
              </div>
              <ErrorMessage name="password" component="div" className="invalid-feedback" />
            </div>

            <div className="form-footer">
              <div className="form-check">
                <Field type="checkbox" name="remember" id="remember" className="form-check-input" />
                <label htmlFor="remember" className="form-check-label">Se souvenir de moi</label>
              </div>
              <Link to="/forgot-password" className="forgot-password">
                Mot de passe oublié ?
              </Link>
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={isSubmitting || loading}
            >
              {loading ? 'Connexion en cours...' : 'Se connecter'}
            </button>
          </Form>
        )}
      </Formik>

      <div className="auth-links">
        <p>
          Vous n'avez pas de compte ?{' '}
          <Link to="/register" className="auth-link">
            Créer un compte
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;